/**
 * Wave 12.4 — PWA hooks.
 *
 * Two surfaces:
 *   - `registerServiceWorker()` fires on app boot and, if the current
 *     environment supports it, registers `/sw.js`. It is tolerant of
 *     every single failure mode (no support, blocked by policy,
 *     network error, parse error in sw.js). The app must work without
 *     it; the worker is just a nicer floor.
 *   - `useInstallPrompt()` wraps the `beforeinstallprompt` event into
 *     a React hook that components can opt into without re-wiring the
 *     global listener.
 *
 * We deliberately do NOT auto-show an install banner. Browser install
 * UX is context-sensitive and inconsistent; instead we expose the
 * captured prompt to a component that picks the right moment to ask
 * (after the user has created their first RDO is a good one — a
 * "you'll want this on your home screen" feels earned, not pushy).
 */

import { useEffect, useState } from 'react';

/**
 * 2026-05-18 — Service worker teardown mode, v2 (defense in depth).
 *
 * Backstory:
 *   1. 2026-05-16 sw.js called clients.navigate(c.url) on activate →
 *      infinite reload loop (110 navs / 218 assets in 8s on /login).
 *   2. 2026-05-17 sw.js: idempotent teardown — install + activate +
 *      unregister + cache wipe, no navigate. pwa.ts paired to STOP
 *      registering after a localStorage marker was set.
 *   3. 2026-05-18 (this revision): JP still seeing loop reports in
 *      prod. Root cause: /sw.js was served by Cloudflare Pages with
 *      `public, max-age=14400, must-revalidate` (4h edge + browser
 *      HTTP cache). Users whose browser still had the 2026-05-16 SW
 *      script cached kept running it for up to 24h. The 2026-05-17
 *      fix was correct, but the patched sw.js could not REACH them.
 *      Fix shipped in `_headers`: /sw.js → `no-cache, no-store,
 *      must-revalidate`. Combined with the changes below the loop is
 *      fully impossible.
 *
 * What this function does, every page load:
 *   a) Always look for stale registrations and unregister them. We do
 *      this on every load now (not just once gated by the marker) — it
 *      is a single async getRegistrations() call which resolves to an
 *      empty array in steady state. Cheap.
 *   b) Wipe any leftover Cache Storage entries the SAME one time only,
 *      gated by the marker, because that walk can be expensive on
 *      devices with thousands of cached assets.
 *   c) NEVER call navigator.serviceWorker.register(). The only way a
 *      new SW gets installed is a future deploy that flips
 *      VITE_PWA_ENABLED=1 explicitly.
 *   d) Listen for `controllerchange` and DO NOTHING. The default
 *      browser behaviour without a listener is fine, but earlier
 *      bundles attached a `window.location.reload()` here which, paired
 *      with skipWaiting+clients.claim, is the canonical SW reload loop.
 *      Attaching an explicit no-op listener shadows any code that may
 *      try to reattach (defensive — should not happen in current src).
 *
 * To re-enable a real SW later: ship a new sw.js under a different
 * cache namespace, register it behind `import.meta.env.VITE_PWA_ENABLED
 * === '1'`, and bump the cache-wipe marker key so this teardown reruns
 * once for users coming from this build.
 */
const CACHE_WIPE_MARKER = 'builder:pwa-cache-wipe-done-v2';

export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  if (import.meta.env.DEV) return;

  // (d) Shadow any rogue controllerchange handler with an explicit no-op.
  // The reference is intentionally not removed — we want it to outlive
  // anything that might later attach a reload handler.
  try {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Intentionally empty. See the docblock above for the reload-loop
      // rationale. Removing this listener is a regression — DO NOT.
    });
  } catch {
    // Some browsers throw on the listener attach when storage is fully
    // blocked. Safe to ignore.
  }

  // FEATURE C2 · 2026-05-21 — listener pra mensagens do SW (background sync).
  // Mantido sempre montado pra ser inerte mesmo quando pwa disabled — se
  // sobrar um SW antigo controlando uma janela velha que envie msg, o
  // handler simplesmente chama flushOutbox (que é seguro / idempotente).
  try {
    navigator.serviceWorker.addEventListener('message', (event) => {
      const data = (event as MessageEvent).data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'builder:outbox-sync') {
        // Lazy import: pwa.ts é importado MUITO cedo no boot, antes de o
        // outbox stack carregar. Evita pull-in do api client no boot path.
        void import('./offline-outbox').then((m) => m.flushOutbox());
      }
    });
  } catch { /* listener attach blocked — ignora */ }

  // FEATURE C2 · 2026-05-21 — opt-in re-register sob VITE_PWA_ENABLED.
  // Default (unset / '0'): mantém o comportamento de teardown abaixo —
  // prod permanece SW-less.
  // Quando '1': pula o teardown e registra o sw.js v3 pra background sync.
  // O env var é estático no build (Vite); flipar exige um deploy.
  const pwaEnabled =
    (import.meta.env.VITE_PWA_ENABLED as string | undefined) === '1';

  window.addEventListener('load', () => {
    void (async () => {
      if (pwaEnabled) {
        // Caminho FEATURE C2 — registra sw.js. Se já houver registration
        // ativa com o mesmo script URL, browser dedup e a chamada é noop.
        try {
          await navigator.serviceWorker.register('/sw.js');
        } catch {
          // Registration falhou (CSP, storage, etc) — outbox cai pro
          // fallback de `online` event manual. App segue funcional.
        }
        return;
      }

      // ── Caminho legado · teardown (default) ────────────────────────
      // (a) Always unregister any stale SW. In steady state this is a
      //     single resolved Promise with an empty array.
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
      } catch {
        // Policy / storage issue — nothing actionable.
      }

      // (b) One-shot cache wipe, gated by marker.
      let alreadyWiped = false;
      try {
        alreadyWiped = localStorage.getItem(CACHE_WIPE_MARKER) === '1';
      } catch {
        // Private mode — treat as not wiped; we'll do the work and skip
        // the marker write at the end.
      }
      if (!alreadyWiped) {
        try {
          if (typeof caches !== 'undefined') {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
          }
        } catch {
          // Cache Storage policy issue — best-effort.
        }
        try {
          localStorage.setItem(CACHE_WIPE_MARKER, '1');
        } catch {
          // No storage — we'll retry next load, harmless.
        }
      }
    })();
  });
}

/**
 * Browsers fire `beforeinstallprompt` only under their own opaque
 * criteria (engagement, manifest quality, no ad-click origin, …). We
 * stash the event early so a UI component mounted later can still
 * trigger `prompt()` on demand.
 */
interface DeferredPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: DeferredPromptEvent | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent Chrome's built-in "Add to home screen" micro-banner so we
    // control the surface that asks.
    e.preventDefault();
    deferred = e as DeferredPromptEvent;
    window.dispatchEvent(new CustomEvent('builder:installable'));
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    try {
      localStorage.setItem('builder:pwa-installed', '1');
    } catch {
      // Private mode or no storage — ignore.
    }
    window.dispatchEvent(new CustomEvent('builder:installed'));
  });
}

export interface InstallPromptState {
  /** The browser has told us the app is installable. */
  canInstall: boolean;
  /** True once the app is running from an installed window (standalone). */
  isStandalone: boolean;
  /** Show the native prompt. Resolves to accepted/dismissed/unavailable. */
  prompt: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
  /** User has asked us not to nag again this session OR within TTL window. */
  dismissed: boolean;
  /** Mark as dismissed (persistent 7d TTL + session flag). */
  dismiss: () => void;
}

/**
 * W124.6 — PWA prompt dismissal policy.
 *
 * Antes: dismissal era sessionStorage-only ('builder:pwa-dismissed'), o que
 * fazia o card "Instalar o Builder no celular?" re-aparecer no próximo F5
 * (cada navegação fresh tab no mobile = card de volta). JP reportou
 * 2026-05-26 que o prompt entrava em todas as navegações e bloqueava CTA
 * do wizard antes do z-index fix do W124.1.
 *
 * Política nova:
 *   - localStorage `builder.pwa-prompt.last-dismissed-at` (ms timestamp)
 *     persiste o "agora não" por 7 dias entre sessions/devices.
 *   - sessionStorage `builder.pwa-prompt.shown-this-session` (1) garante
 *     no máximo 1 vez por aba mesmo que TTL tenha expirado mid-session.
 *   - Após 7d expira e voltamos a perguntar (única vez na próxima session).
 */
const DISMISS_KEY = 'builder.pwa-prompt.last-dismissed-at';
const SHOWN_KEY = 'builder.pwa-prompt.shown-this-session';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function isWithinDismissTtl(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function wasShownThisSession(): boolean {
  try {
    return sessionStorage.getItem(SHOWN_KEY) === '1';
  } catch {
    return false;
  }
}

function markShownThisSession(): void {
  try {
    sessionStorage.setItem(SHOWN_KEY, '1');
  } catch {
    // Private mode — re-shows are limited by canInstall race anyway.
  }
}

export function useInstallPrompt(): InstallPromptState {
  const [canInstall, setCanInstall] = useState<boolean>(!!deferred);
  const [dismissed, setDismissed] = useState<boolean>(
    () => isWithinDismissTtl() || wasShownThisSession(),
  );

  useEffect(() => {
    const onInstallable = () => setCanInstall(true);
    const onInstalled = () => setCanInstall(false);
    window.addEventListener('builder:installable', onInstallable);
    window.addEventListener('builder:installed', onInstalled);
    return () => {
      window.removeEventListener('builder:installable', onInstallable);
      window.removeEventListener('builder:installed', onInstalled);
    };
  }, []);

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      // iOS Safari quirk — it does not honor display-mode, using the
      // non-standard `navigator.standalone` instead.
      (window.navigator as unknown as { standalone?: boolean }).standalone === true);

  // W124.6: tão logo o componente decide renderizar (canInstall + outras
  // gates passam), marca shown-this-session pra impedir re-render numa
  // navegação subsequente após user fechar a aba e voltar no mesmo tab.
  useEffect(() => {
    if (canInstall && !isStandalone && !dismissed) {
      markShownThisSession();
    }
  }, [canInstall, isStandalone, dismissed]);

  const prompt = async () => {
    if (!deferred) return 'unavailable' as const;
    try {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      deferred = null;
      setCanInstall(false);
      return outcome;
    } catch {
      return 'unavailable' as const;
    }
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // No storage — at least dismiss for this render pass.
    }
    markShownThisSession();
    setDismissed(true);
  };

  return { canInstall, isStandalone, prompt, dismissed, dismiss };
}
